import { useState, useEffect } from "react";
import { apiPost, apiGetAll } from "../../api";

export default function CreateTaskModal({ taskTypeOptions, onClose, onCreated }) {
  const [allClients, setAllClients] = useState([]);
  const [clientQuery, setClientQuery] = useState('');
  const [clientId, setClientId] = useState(null);
  const [category, setCategory] = useState('corporate');
  const [fiscalYear, setFiscalYear] = useState(new Date().getFullYear());
  const [taskTypeId, setTaskTypeId] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    apiGetAll('/api/clients/?is_active=true').then(setAllClients).catch(() => setAllClients([]));
  }, []);

  const matches = clientQuery.trim()
    ? allClients.filter(c => (c.name || '').toLowerCase().includes(clientQuery.toLowerCase())).slice(0, 12)
    : allClients.slice(0, 12);

  const submit = async (e) => {
    e.preventDefault();
    if (!clientId) { setErr('Pick a client'); return; }
    setBusy(true); setErr('');
    try {
      const created = await apiPost('/api/tasks/', {
        client: clientId,
        task_category: category,
        fiscal_year: fiscalYear ? parseInt(fiscalYear, 10) : null,
        task_type: taskTypeId ? parseInt(taskTypeId, 10) : null,
      });
      onCreated(created);
    } catch (e2) {
      // The Task model enforces one OPEN task per (client, category, fiscal year)
      // via the `unique_open_task_per_client_year_type` constraint. DRF surfaces
      // that as a cryptic `non_field_errors: "...must make a unique set."` — turn
      // it into something actionable.
      const nf = e2.response?.data?.non_field_errors;
      const dupMsg = Array.isArray(nf) ? nf.join(' ') : (nf || '');
      if (/unique set/i.test(dupMsg)) {
        const catLabel = category === 'corporate' ? 'Corporate (T2)' : 'Personal (T1)';
        setErr(
          `This client already has an open ${catLabel} task for FY ${fiscalYear}. ` +
          `Complete that task first, or pick a different fiscal year.`
        );
      } else {
        setErr(e2.message || 'Failed to create');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: 8, width: 460, padding: 24 }}>
        <h3 style={{ marginTop: 0 }}>New Task</h3>
        <form onSubmit={submit}>
          <div style={{ marginBottom: 12, position: 'relative' }}>
            <label style={modalLabel}>Client</label>
            <input
              type="text"
              value={clientQuery}
              onChange={(e) => { setClientQuery(e.target.value); setClientId(null); }}
              placeholder="Type to search…"
              autoFocus
              style={modalInput}
            />
            {clientQuery && !clientId && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #d1d5db', borderRadius: 4, marginTop: 2, maxHeight: 200, overflow: 'auto', zIndex: 10 }}>
                {matches.length === 0 ? (
                  <div style={{ padding: 8, color: '#9ca3af', fontSize: 13 }}>No matches</div>
                ) : matches.map(c => (
                  <div
                    key={c.id}
                    onClick={() => { setClientId(c.id); setClientQuery(c.name); setCategory(c.is_corporate ? 'corporate' : 'personal'); }}
                    style={{ padding: '6px 10px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f3f4f6' }}
                  >
                    {c.name} <span style={{ color: '#9ca3af' }}>({c.is_corporate ? 'Corporate' : 'Personal'})</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={modalLabel}>Category</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)} style={modalInput}>
              <option value="corporate">Corporate (T2)</option>
              <option value="personal">Personal (T1)</option>
            </select>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={modalLabel}>Fiscal Year</label>
            <input type="number" value={fiscalYear} onChange={(e) => setFiscalYear(e.target.value)} style={modalInput} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={modalLabel}>Task Type (optional)</label>
            <select value={taskTypeId} onChange={(e) => setTaskTypeId(e.target.value)} style={modalInput}>
              <option value="">— None —</option>
              {taskTypeOptions.map(tt => (
                <option key={tt.id} value={tt.id}>{tt.name}</option>
              ))}
            </select>
          </div>
          {err && <div style={{ color: '#dc2626', marginBottom: 12, fontSize: 13 }}>{err}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} disabled={busy} style={{ padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 4, background: 'white', cursor: 'pointer' }}>Cancel</button>
            <button type="submit" disabled={busy} style={{ padding: '6px 12px', border: 'none', borderRadius: 4, background: '#059669', color: 'white', cursor: 'pointer', fontWeight: 600 }}>{busy ? 'Creating…' : 'Create Task'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

const modalLabel = { display: 'block', fontSize: 12, color: '#374151', marginBottom: 4 };
const modalInput = { width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 14, background: 'white', boxSizing: 'border-box' };
